import React from 'react'
import ReactPlayer from 'react-player'
import { IonGrid, IonRow, isPlatform, IonCol } from '@ionic/react'
import classNames from 'classnames'
import {
	ScreenSectionItem,
	formatTimeMS,
	SectionMediaVideoContainer as Container,
	SCREEN_SECTION_TYPE_WEB_VIDEO,
	ScreenSection,
	Screen,
} from 'mytours-read-common'
import Slider from '../../../common/components/AccessibleSlider'
import { isRTL } from '../../../platform'

import Loader from '../../../common/components/Loader'
import { Button } from '@mytours/components'

import { SwiperClass } from './Slider'
import { ImageSizePreset } from '../../../../common_lib/types'
import { generateImageSrcSet, generateImageUrl, generateVideoUrl } from '../../../../common_lib/utils'
import {
	getVideoUrl,
	getYoutubeThumbnailUrl,
	isRemoteVideo,
	ReactPlayerConfig,
	sendVideoPauseEvent,
	sendVideoPlayEvent,
	sendVideoProgressEvent,
} from './functions'

import strings from '../../../../strings'
import '../styles/fullScreenVideo.less'
import { getImageAlt } from 'mobile_web/modules/common/functions'

interface OwnProps {
	sectionItem: ScreenSectionItem
	section: ScreenSection
	screen: Screen
	sliderRef: SwiperClass
	onBack: () => void
	hasPrev: boolean
	hasNext: boolean
	slideIndex: number
	autoplay?: boolean
	isInLoopSlider?: boolean
}

interface State {
	isPaused: boolean
	showControls: boolean
	progress: number
	duration?: number
	userDraggingSlider: boolean
	showArrowButtons: boolean
}

type Props = OwnProps & Container.Props & Container.Actions

/**
 * The initial state for our internal component state.
 */
const INITIAL_STATE: State = {
	isPaused: true,
	showControls: true,
	userDraggingSlider: false,
	progress: 0,
	showArrowButtons: true,
}

interface VideoProgressData {
	played: number
	playedSeconds: number
	loaded: number
	loadedSeconds: number
}

class SectionMediaGroupVideo extends React.Component<Props, State> {
	player: ReactPlayer | null
	isVideoStarted = false

	private progressBreakpoints = {
		10: false,
		25: false,
		50: false,
		75: false,
	}

	constructor(props) {
		super(props)

		this.state = {
			...INITIAL_STATE,
			isPaused: !props.autoplay,
		}
	}

	componentDidMount() {
		const {
			sectionItem: { file },
			videoFilePath,
		} = this.props

		if (!videoFilePath && file) {
			const url = generateVideoUrl(file, {
				pathType: file.dont_transform ? 'original' : 'video_generic480p',
			})
			const filename = file.filename
			this.props.loadVideoPath(filename, url)
		}
	}

	componentDidUpdate() {
		this.pauseSlider()
	}

	private pauseSlider = async () => {
		const { slideIndex, sliderRef } = this.props

		if (sliderRef) {
			const { isPaused } = this.state
			const activeIndex = sliderRef.loopedSlides
				? sliderRef.activeIndex - sliderRef.loopedSlides
				: sliderRef.activeIndex

			if (slideIndex !== activeIndex && !isPaused) {
				this.setState(() => ({ isPaused: true }))
			}
		}
	}

	_isLoadingVideoPath(): boolean {
		const {
			videoFilePath,
			loadingVideoPath,
			sectionItem: { media_type: mediaType },
		} = this.props
		if (mediaType === SCREEN_SECTION_TYPE_WEB_VIDEO) {
			return false
		}

		return loadingVideoPath || videoFilePath === undefined || videoFilePath === null
	}

	onPlayPausePress = () => {
		this.setState(prev => ({ isPaused: !prev.isPaused }))
		if (this.state.progress === 100) {
			this.setState({ showArrowButtons: true })
		}
	}

	get videoEventParams() {
		const { sectionItem, section, screen } = this.props

		return {
			sectionItemName: sectionItem.name,
			sectionId: section.id,
			sectionTitle: section.name,
			containerName: screen.name,
		}
	}

	onPlay = () => {
		this.setState({ isPaused: false })
		setTimeout(() => {
			this.setState({ showControls: false })
		}, 1000)

		sendVideoPlayEvent(this.player, this.videoEventParams, this.isVideoStarted)

		this.isVideoStarted = true
	}

	onPause = () => {
		sendVideoPauseEvent(this.player, this.videoEventParams)

		this.setState({ isPaused: true })
	}

	_onSliderValueChanged = (value: number) => {
		// update user dragging state (but only if we need to as this callback gets hit a lot while dragging the slider)
		if (!this.state.userDraggingSlider) {
			this.setState({ userDraggingSlider: true })
		}

		if (value === 100) {
			this.player.seekTo(this.state.duration)
			this.setState({ isPaused: true, showArrowButtons: false })
		} else if (value === 0) {
			this.setState({ showArrowButtons: true })
		}

		this.setState({ progress: value })
	}

	_onSlidingComplete = () => {
		const { progress } = this.state
		const value = Math.round(progress)
		const { duration } = this.state
		if (this.player && duration) {
			const newTime = duration * (value / 100)

			if (newTime === duration) {
				this.player.seekTo(newTime)
				this.setState({ isPaused: true, showArrowButtons: false })
			} else {
				this.player.seekTo(newTime)
			}
		}

		this.setState({ userDraggingSlider: false }, () => {
			const { sliderRef } = this.props
			if (sliderRef) {
				sliderRef.enabled = true
			}
		})
	}

	_onProgress = (data: VideoProgressData) => {
		const { userDraggingSlider, progress } = this.state
		const videoDuration = this.player.getDuration() || 1
		const newProgress = userDraggingSlider ? progress : (data.playedSeconds / videoDuration) * 100

		sendVideoProgressEvent(this.player, this.videoEventParams, data.played, this.progressBreakpoints)

		if (newProgress === progress) return

		if (newProgress === 100) {
			this.player.seekTo(videoDuration)
			this.setState({ isPaused: true, progress: 100, showArrowButtons: false })
		} else {
			this.setState({
				duration: videoDuration,
				progress: newProgress,
				showArrowButtons: true,
			})
		}
	}

	private toggleControls = () => {
		this.setState((prev: State) => ({ showControls: !prev.showControls }))
	}

	handleControls = e => {
		const {
			target: { attributes, parentElement },
		} = e
		const hasAttribute = attributes?.['player-action-attribute']
		const parentHasAttribute = parentElement?.attributes?.['player-action-attribute']

		if (hasAttribute || parentHasAttribute) {
			this.setState({ showControls: true })
		} else {
			this.toggleControls()
		}
	}

	disableSlider = () => {
		const { sliderRef } = this.props
		if (sliderRef) {
			sliderRef.enabled = false
		}
	}

	enableSlider = () => {
		const { sliderRef } = this.props
		if (sliderRef) {
			sliderRef.enabled = true
		}
	}

	onVideoEnd = () => {
		this.setState({ isPaused: true, showArrowButtons: true })

		this.isVideoStarted = false
		this.progressBreakpoints = {
			10: false,
			25: false,
			50: false,
			75: false,
		}
	}

	onPrev = () => {
		const { sliderRef } = this.props
		if (!sliderRef.isBeginning) {
			this.setState(() => ({ isPaused: true }))
			sliderRef.slidePrev()
		}
	}

	onNext = () => {
		const { sliderRef } = this.props

		if (!sliderRef.isEnd) {
			this.setState(() => ({ isPaused: true }))
			sliderRef.slideNext()
		}
	}

	get getImageUrl() {
		const {
			sectionItem: { thumbnail_media_item: thumbnailMediaItem, media_type, remote_type, remote_id },
		} = this.props

		const url = thumbnailMediaItem?.file
			? generateImageUrl(thumbnailMediaItem?.file, {
					imageSizePreset: ImageSizePreset.Row,
				})
			: getYoutubeThumbnailUrl({ media_type, remote_type, remote_id })

		return url
	}

	get getImageSrcSet() {
		const {
			sectionItem: { thumbnail_media_item: thumbnailMediaItem, media_type, remote_type, remote_id },
		} = this.props

		const srcSet = thumbnailMediaItem?.file
			? generateImageSrcSet(thumbnailMediaItem?.file, {
					imageSizePreset: ImageSizePreset.Row,
				})
			: getYoutubeThumbnailUrl({ media_type, remote_type, remote_id })

		return srcSet
	}

	render() {
		const { videoFilePath, onBack, hasPrev, hasNext, sectionItem } = this.props
		const { isPaused, showControls, progress, showArrowButtons, duration } = this.state

		const isLoadingVideoPath = this._isLoadingVideoPath()

		const currentTime = this.player && this.player.getCurrentTime()

		// format time
		const elapsedTime: number = currentTime ? Math.round(currentTime) : 0
		const formattedDuration = duration ? Math.round(duration) : 0
		const formattedElapsedTime: string = formatTimeMS(elapsedTime)
		const formattedDurationTime: string = formatTimeMS(formattedDuration)

		const url = getVideoUrl(videoFilePath, sectionItem)
		const isEmbed = isRemoteVideo(sectionItem.remote_type)
		const showVideoControls = showControls && !isEmbed

		const showRightArrow = (hasNext && showArrowButtons) || !showArrowButtons
		const showLeftArrow = (hasPrev && showArrowButtons) || !showArrowButtons

		return (
			<IonGrid
				className={classNames('fullscreen-video-container group-video', {
					embed: isEmbed,
				})}
			>
				<IonRow className="fullscreen-video-row" onClick={this.handleControls}>
					<IonCol>
						{isLoadingVideoPath && (
							<IonRow className="loading-row">
								{/* The '' fallback keeps the attribute present: an `&&` here yields undefined when
								    there is no thumbnail metadata, and React then drops `alt` altogether, leaving
								    the cover image unlabelled rather than marked decorative. */}
								<img
									src={this.getImageUrl}
									srcSet={this.getImageSrcSet}
									className="video-cover-image"
									alt={sectionItem?.thumbnail_media_item ? getImageAlt(sectionItem.thumbnail_media_item) : ''}
								/>
								<IonRow>
									<Loader />
								</IonRow>
							</IonRow>
						)}
						<IonRow className="player-row">
							<IonRow className="player-row-inner">
								<ReactPlayer
									url={url}
									playing={!isPaused}
									onProgress={this._onProgress}
									ref={(ref: ReactPlayer): ReactPlayer => (this.player = ref)}
									width="100%"
									height="100%"
									onPlay={this.onPlay}
									onPause={this.onPause}
									onEnded={this.onVideoEnd}
									config={ReactPlayerConfig}
								/>
								{showVideoControls && (
									<div className="controls-wrapper">
										<div className="controls">
											{showLeftArrow ? (
												<Button
													player-action-attribute="player-action-attribute"
													className="control-button left-arrow media-action-click"
													variant="clear"
													onClick={showArrowButtons ? this.onPrev : onBack}
													aria-label={showArrowButtons ? strings.accessibility_previous : strings.finish}
												>
													{showArrowButtons ? <i className="fas fa-step-backward" /> : <i className="fal fa-xmark" />}
												</Button>
											) : null}
											<Button
												className={classNames(
													'control-button media-action-click',
													showArrowButtons && (isPaused ? 'play' : 'pause'),
													{
														'right-arrow': !showRightArrow && showLeftArrow,
														'left-arrow': !showLeftArrow && showRightArrow,
													},
												)}
												variant="clear"
												onClick={this.onPlayPausePress}
												player-action-attribute="player-action-attribute"
												aria-label={isPaused ? strings.play_media : strings.pause_media}
											>
												<i className={`fas fa-${this.state.isPaused ? 'play' : 'pause'}`} />
											</Button>
											{showRightArrow ? (
												<Button
													className="control-button right-arrow media-action-click"
													player-action-attribute="player-action-attribute"
													variant="clear"
													onClick={showArrowButtons ? this.onNext : this.onPlayPausePress}
													aria-label={showArrowButtons ? strings.accessibility_next : strings.accessibility_play_again}
												>
													{showArrowButtons ? <i className="fas fa-step-forward" /> : <i className="far fa-undo" />}
												</Button>
											) : null}
										</div>
									</div>
								)}
							</IonRow>
						</IonRow>
						{showVideoControls && (
							<IonRow className="video-range-row" style={{ paddingBottom: isPlatform('ios') ? 10 : 20 }}>
								<IonCol className="duration-col">{formattedElapsedTime} </IonCol>
								<IonCol onMouseOver={this.disableSlider} onMouseLeave={this.enableSlider}>
									<Slider
										className={`slider-range ${isPlatform('ios') && 'slider-range-ios'}`}
										ariaLabel={strings.accessibility_video_seek}
										max={100}
										min={0}
										value={progress}
										tooltip={false}
										onChange={this._onSliderValueChanged}
										onChangeComplete={this._onSlidingComplete}
										reverse={isRTL()}
									/>
								</IonCol>
								<IonCol className="duration-col">{formattedDurationTime}</IonCol>
							</IonRow>
						)}
					</IonCol>
				</IonRow>
			</IonGrid>
		)
	}
}

const SectionMediaGroupVideoComponent = Container.default(SectionMediaGroupVideo)
export default SectionMediaGroupVideoComponent
